/**
 * Website chat widgets — Super-Admin state.
 *
 * One record per embeddable widget. The reason this page exists is the knowledge
 * selection: which documents from the public corpus a given widget may answer
 * from. Everything else (greeting, prompts, theme, schedule) is configuration
 * that used to require a deploy.
 *
 * Edits are held locally as a `draft` and only sent on save, because a widget's
 * configuration is a coherent whole — saving each checkbox as it is ticked would
 * publish a half-chosen knowledge set to a live public site.
 */
import { create } from 'zustand';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import type {
  ChatBrandDocumentDto,
  ChatBrandDto,
  ChatKnowledgeDoc,
  ChatKnowledgeDocsResponse,
  CreateChatBrandInput,
  UpdateChatBrandInput,
} from '@respectly/shared-types';

const E = API_ENDPOINTS.SUPER_ADMIN.CHAT;

/**
 * Files per request. Matches the gateway's own per-request ceiling; a bigger
 * drop is split rather than refused.
 */
const UPLOAD_BATCH_SIZE = 100;

/**
 * Keep the list row's document count in step with the panel below it.
 *
 * The row reads `ownDocumentCount` off the brands list, which is fetched once.
 * Without this, uploading to a widget leaves its row still reading "No documents
 * — answers nothing" until a full reload, which is the message that made a
 * successful upload look like a failed one.
 */
function withOwnCount(
  brands: ChatBrandDto[],
  brandId: string,
  documents: ChatBrandDocumentDto[]
): ChatBrandDto[] {
  const ownDocumentCount = documents.filter((d) => d.isActive).length;
  return brands.map((b) => (b.brandId === brandId ? { ...b, ownDocumentCount } : b));
}

/** What an upload reports back — per file, so a partial success is visible. */
interface UploadResult {
  saved: string[];
  rejected: Array<{ filename: string; message: string }>;
  documents: ChatBrandDocumentDto[];
}

/** The editable slice of a widget. Mirrors `UpdateChatBrandInput`. */
export type ChatBrandDraft = UpdateChatBrandInput;

interface ChatWidgetsState {
  brands: ChatBrandDto[];
  selectedBrandId: string | null;

  docs: ChatKnowledgeDoc[];
  /** False when the corpus service is unreachable — NOT the same as "no docs". */
  knowledgeConfigured: boolean;
  /**
   * Whether the retrieval service can fetch a widget's OWN uploaded documents.
   * Separate from `knowledgeConfigured`, and separately breakable — in production
   * this leg was unwired while the other stayed green, so documents were listed
   * here and never searched. `null` = could not be determined.
   */
  brandCorpusConfigured: boolean | null;
  /** The shared base prompt, so the per-widget editor can start from it rather than blank. */
  defaultPrompt: string | null;
  knowledgeLoaded: boolean;

  /** Unsaved edits for the selected widget, keyed by field. */
  draft: ChatBrandDraft;

  /**
   * The selected widget's own uploaded documents.
   *
   * Not part of `draft`. Uploads commit the moment they land, because a file
   * shown in a list that has not actually been saved is a lie — and unlike a
   * greeting, an upload is not something you can hold in your head while you
   * decide whether to keep it.
   */
  documents: ChatBrandDocumentDto[];
  /**
   * The widget `documents` actually belongs to, or null when they have not been
   * fetched yet.
   *
   * An empty `documents` is ambiguous on its own — "this widget has none" and
   * "we have not asked yet" look identical — and the knowledge picker draws a
   * verdict from that count. Reading it before the fetch landed made it announce
   * "this widget will answer from nothing" to an admin who had just uploaded 23
   * files. This says which of the two it is.
   */
  documentsLoadedFor: string | null;
  loadingDocuments: boolean;
  uploading: boolean;

  loading: boolean;
  loadingDocs: boolean;
  saving: boolean;
  error: string | null;

  /** `preferBrandId` is the widget the URL asks for, e.g. after a reload. */
  load: (preferBrandId?: string | null) => Promise<void>;
  loadDocs: () => Promise<void>;
  loadDocuments: (brandId: string) => Promise<void>;
  uploadDocuments: (
    brandId: string,
    files: File[]
  ) => Promise<{ saved: string[]; rejected: Array<{ filename: string; message: string }> } | null>;
  setDocumentActive: (brandId: string, id: string, isActive: boolean) => Promise<void>;
  removeDocument: (brandId: string, id: string) => Promise<void>;
  select: (brandId: string | null) => void;
  /** Merge a partial edit into the draft. */
  edit: (patch: ChatBrandDraft) => void;
  discard: () => void;
  save: () => Promise<boolean>;
  create: (input: CreateChatBrandInput) => Promise<ChatBrandDto | null>;
  setActive: (brandId: string, isActive: boolean) => Promise<void>;
}

function message(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

export const useChatWidgetsStore = create<ChatWidgetsState>((set, get) => ({
  brands: [],
  selectedBrandId: null,
  docs: [],
  knowledgeConfigured: true,
  brandCorpusConfigured: null,
  defaultPrompt: null,
  knowledgeLoaded: false,
  documents: [],
  documentsLoadedFor: null,
  loadingDocuments: false,
  uploading: false,
  draft: {},
  loading: false,
  loadingDocs: false,
  saving: false,
  error: null,

  load: async (preferBrandId) => {
    set({ loading: true, error: null });
    try {
      const api = getApiClient();
      const brands = (await api.get<ChatBrandDto[]>(E.BRANDS)) ?? [];
      const exists = (id: string | null | undefined) =>
        !!id && brands.some((b) => b.brandId === id);

      // The widget named in the URL wins — a reload has no in-memory selection,
      // and silently opening the first widget instead makes another widget's
      // (empty) document list look like your uploads vanished. Then the current
      // selection if it survived the reload; then the first, so the page is never
      // an empty shell with a list beside it.
      const current = get().selectedBrandId;
      const keepCurrent = !exists(preferBrandId) && exists(current);
      const selectedBrandId = exists(preferBrandId)
        ? preferBrandId!
        : keepCurrent
          ? current
          : (brands[0]?.brandId ?? null);

      set({
        brands,
        loading: false,
        selectedBrandId,
        draft: keepCurrent ? get().draft : {},
      });
    } catch (err) {
      set({ loading: false, error: message(err, 'Failed to load chat widgets') });
    }
  },

  loadDocs: async () => {
    set({ loadingDocs: true });
    try {
      const api = getApiClient();
      const result = await api.get<ChatKnowledgeDocsResponse>(E.KNOWLEDGE_DOCS);
      // Best-effort: the prompt editor falls back to a plain box without it.
      api
        .get<{ prompt: string }>(E.DEFAULT_PROMPT)
        .then((d) => set({ defaultPrompt: d?.prompt ?? null }))
        .catch(() => undefined);
      set({
        docs: result?.docs ?? [],
        knowledgeConfigured: result?.configured ?? false,
        brandCorpusConfigured: result?.brandCorpusConfigured ?? null,
        knowledgeLoaded: true,
        loadingDocs: false,
      });
    } catch (err) {
      // Treat a failed fetch the same as an unreachable service: the picker must
      // say so rather than render an empty list that reads as "no documents".
      set({
        loadingDocs: false,
        knowledgeConfigured: false,
        brandCorpusConfigured: null,
        knowledgeLoaded: true,
        error: message(err, 'Failed to load the knowledge corpus'),
      });
    }
  },

  loadDocuments: async (brandId) => {
    // Drop another widget's list rather than showing it under this one's name.
    set({
      loadingDocuments: true,
      ...(get().documentsLoadedFor === brandId ? {} : { documents: [], documentsLoadedFor: null }),
    });
    try {
      const api = getApiClient();
      const documents = (await api.get<ChatBrandDocumentDto[]>(E.BRAND_DOCUMENTS(brandId))) ?? [];
      // A slower response for a widget the admin has already navigated away from
      // must not overwrite the one now on screen.
      if (get().selectedBrandId !== brandId) return;
      set({
        documents,
        documentsLoadedFor: brandId,
        loadingDocuments: false,
        brands: withOwnCount(get().brands, brandId, documents),
      });
    } catch (err) {
      set({ loadingDocuments: false, error: message(err, 'Failed to load this widget’s documents') });
    }
  },

  uploadDocuments: async (brandId, files) => {
    set({ uploading: true, error: null });
    try {
      const api = getApiClient();
      const all = Array.from(files);
      const saved: string[] = [];
      const rejected: Array<{ filename: string; message: string }> = [];
      let documents = get().documents;

      // Sent in batches, because one multipart body carrying an entire folder is
      // a request the gateway refuses whole — the failure people hit was a drop
      // of eleven files saving none of them. Each batch commits on its own, so a
      // large upload lands progressively instead of all-or-nothing.
      for (let i = 0; i < all.length; i += UPLOAD_BATCH_SIZE) {
        const form = new FormData();
        for (const file of all.slice(i, i + UPLOAD_BATCH_SIZE)) form.append('files', file);
        // Multipart, so no JSON content-type: the browser has to set the boundary.
        const result = await api.post<UploadResult>(E.BRAND_DOCUMENTS(brandId), form);
        if (!result) continue;
        saved.push(...result.saved);
        rejected.push(...result.rejected);
        documents = result.documents;
        // Show each batch as it lands rather than freezing the list until the last.
        set({ documents, brands: withOwnCount(get().brands, brandId, documents) });
      }

      set({ uploading: false });
      return { saved, rejected };
    } catch (err) {
      set({ uploading: false, error: message(err, 'Failed to upload') });
      return null;
    }
  },

  setDocumentActive: async (brandId, id, isActive) => {
    try {
      const api = getApiClient();
      const documents = await api.patch<ChatBrandDocumentDto[]>(
        E.BRAND_DOCUMENT(brandId, id),
        { isActive }
      );
      if (documents) set({ documents, brands: withOwnCount(get().brands, brandId, documents) });
    } catch (err) {
      set({ error: message(err, 'Failed to update the document') });
    }
  },

  removeDocument: async (brandId, id) => {
    try {
      const api = getApiClient();
      const documents = await api.delete<ChatBrandDocumentDto[]>(E.BRAND_DOCUMENT(brandId, id));
      if (documents) set({ documents, brands: withOwnCount(get().brands, brandId, documents) });
    } catch (err) {
      set({ error: message(err, 'Failed to remove the document') });
    }
  },

  select: (brandId) =>
    set({
      selectedBrandId: brandId,
      draft: {},
      error: null,
      documents: [],
      documentsLoadedFor: null,
    }),

  edit: (patch) => set({ draft: { ...get().draft, ...patch } }),

  discard: () => set({ draft: {} }),

  save: async () => {
    const { selectedBrandId, draft } = get();
    if (!selectedBrandId || Object.keys(draft).length === 0) return true;
    set({ saving: true, error: null });
    try {
      const api = getApiClient();
      const updated = await api.patch<ChatBrandDto>(E.BRAND(selectedBrandId), draft);
      if (updated) {
        set({
          brands: get().brands.map((b) => (b.brandId === selectedBrandId ? updated : b)),
          draft: {},
          saving: false,
        });
      } else {
        set({ saving: false, draft: {} });
      }
      return true;
    } catch (err) {
      set({ saving: false, error: message(err, 'Failed to save the widget') });
      return false;
    }
  },

  create: async (input) => {
    set({ saving: true, error: null });
    try {
      const api = getApiClient();
      const created = await api.post<ChatBrandDto>(E.BRANDS, input);
      if (created) {
        set({
          brands: [...get().brands, created],
          selectedBrandId: created.brandId,
          draft: {},
          saving: false,
        });
        return created;
      }
      set({ saving: false });
      return null;
    } catch (err) {
      set({ saving: false, error: message(err, 'Failed to create the widget') });
      return null;
    }
  },

  setActive: async (brandId, isActive) => {
    // Sent immediately rather than held in the draft: this is the kill switch,
    // and a kill switch that needs a second click to take effect is not one.
    try {
      const api = getApiClient();
      const updated = await api.patch<ChatBrandDto>(E.BRAND(brandId), { isActive });
      if (updated) {
        set({ brands: get().brands.map((b) => (b.brandId === brandId ? updated : b)) });
      }
    } catch (err) {
      set({ error: message(err, 'Failed to change the widget status') });
    }
  },
}));
